import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft, AlertCircle, Loader2,
  Calendar,
  TrendingDown, RefreshCw, LayoutDashboard, Search,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { scanApi } from '../api/client'
import type { ReportPayload, CategoryKey, PageSpeedResult, PageSpeedMetric, ScoreBreakdown } from '../types'
import { CategorySidebar } from '../components/scoring/CategorySidebar'
import { CategorySection } from '../components/report/CategorySection'
import { CartleyWebsitePreview } from '../components/report/PhoneMockup'
import { ReportOverviewGrid } from '../components/report/ReportOverviewGrid'
import { RecommendationsSection } from '../components/report/RecommendationsSection'
import { ReviewIntelligenceSection, ReviewRatingFallback } from '../components/report/ReviewIntelligenceSection'
import { PhotosSection } from '../components/report/PhotosSection'
import { SearchRankingsSection } from '../components/report/SearchRankingsSection'
import { LocalListingsProfileCard } from '../components/report/LocalListingsProfileCard'
import {
  ReportSectionCard,
  ReportSectionHeader,
  HeaderMetaChip,
} from '../components/report/ReportSectionCard'
import { APP_NAME, BRAND, CONSULTATION_URL } from '../config'
import { useI18nNavigate } from '../hooks/useI18nNavigate'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import logoHeader from '../assets/landing/logo-header.svg'
import { PhoneVerificationGate } from '../components/auth/PhoneVerificationGate'
import { useAuthStore } from '../stores/authStore'
import { ImproveWebsiteCTA } from '../site-builder'

const CATEGORY_ORDER: CategoryKey[] = ['search_results', 'guest_experience', 'local_listings']

// ─── Revenue loss estimator ───────────────────────────────────────────────────
function estimateRevenueLoss(report: ReportPayload): {
  amount: number
  period: 'daily' | 'monthly' | 'yearly'
  focus: 'unranked' | 'competitors' | 'issues' | 'score_gap' | 'growth'
  unranked: number
  competitors: number
  issues: number
  scoreGap: number
} {
  const category = (report.profile?.category ?? '').toLowerCase()
  const score = report.overall_score ?? 50

  let baseMonthly = 12000
  if (category.includes('restaurant') || category.includes('food') || category.includes('pizza') || category.includes('taco') || category.includes('cafe') || category.includes('coffee')) {
    baseMonthly = 18000
  } else if (category.includes('retail') || category.includes('store') || category.includes('shop')) {
    baseMonthly = 22000
  } else if (category.includes('salon') || category.includes('spa') || category.includes('beauty') || category.includes('nail')) {
    baseMonthly = 9000
  } else if (category.includes('gym') || category.includes('fitness') || category.includes('yoga')) {
    baseMonthly = 14000
  } else if (category.includes('hotel') || category.includes('inn') || category.includes('motel')) {
    baseMonthly = 35000
  } else if (category.includes('dentist') || category.includes('doctor') || category.includes('medical') || category.includes('clinic')) {
    baseMonthly = 28000
  }

  const scoreLossFactor = Math.max(0, (70 - score)) / 100
  const unrankedCount = (report.search_rankings ?? []).filter(r => !r.is_ranked).length
  const issuesCount = Object.values(report.scorecard_result?.category_scores ?? {}).reduce((sum, cat) => {
    return sum + (cat.breakdowns?.filter(b => !b.is_positive).length ?? 0)
  }, 0)
  const scoreGap = Math.max(0, 85 - score)
  const competitorsCount = report.competitors?.length ?? 0
  const isAllClear = unrankedCount === 0 && competitorsCount === 0 && issuesCount === 0
  const focus: 'unranked' | 'competitors' | 'issues' | 'score_gap' | 'growth' = isAllClear
    ? 'growth'
    : unrankedCount > 0
      ? 'unranked'
      : competitorsCount > 0
        ? 'competitors'
        : issuesCount > 0
          ? 'issues'
          : 'score_gap'
  const keywordPenalty = unrankedCount * 350
  const competitorPenalty = (report.competitors?.length ?? 0) * 200
  const rawMonthly = Math.round((baseMonthly * scoreLossFactor + keywordPenalty + competitorPenalty) / 100) * 100
  const monthlyLoss = Math.max(800, rawMonthly)

  if (monthlyLoss >= 5000) {
    return { amount: monthlyLoss, period: 'monthly', focus, unranked: unrankedCount, competitors: competitorsCount, issues: issuesCount, scoreGap }
  }
  if (monthlyLoss >= 1000) {
    return { amount: Math.round(monthlyLoss / 30 / 50) * 50, period: 'daily', focus, unranked: unrankedCount, competitors: competitorsCount, issues: issuesCount, scoreGap }
  }
  return { amount: monthlyLoss * 12, period: 'yearly', focus, unranked: unrankedCount, competitors: competitorsCount, issues: issuesCount, scoreGap }
}

function formatCurrency(amount: number): string {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1).replace(/\.0$/, '')}K`
  }
  return `$${amount.toLocaleString()}`
}

// ─── Revenue Loss Banner ──────────────────────────────────────────────────────

function RevenueLossBanner({ report }: { report: ReportPayload }) {
  const { t } = useTranslation('report')
  const { amount, period, focus, unranked, competitors, issues, scoreGap } = estimateRevenueLoss(report)
  const description = t(`revenue.description.${focus}`, { unranked, competitors, issues, scoreGap })
  const fixMessage = t(`revenue.cartleyFix.${focus}`, { appName: APP_NAME })

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="rounded-2xl overflow-hidden mb-8"
      style={{ background: `linear-gradient(135deg, ${BRAND.navy} 0%, #03224a 100%)` }}
    >
      <div className="px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${BRAND.red}30` }}>
          <TrendingDown className="w-5 h-5" style={{ color: BRAND.red }} />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-white text-sm font-medium">{t('revenue.losing')}</span>
            <span className="text-3xl font-bold" style={{ color: BRAND.red }}>
              {formatCurrency(amount)}
            </span>
            <span className="text-white text-sm font-medium">{t(`revenue.period.${period}`)}</span>
          </div>
          <p className="text-xs text-gray-300 mt-1 leading-relaxed">{description}</p>
          <p className="text-xs text-gray-200 mt-2 font-medium">
            {fixMessage}
          </p>
        </div>
        <a
          href={CONSULTATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 flex items-center justify-center gap-2 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap max-sm:w-full max-sm:min-h-11"
          style={{ backgroundColor: BRAND.red }}
          onMouseOver={e => (e.currentTarget.style.backgroundColor = BRAND.redHover)}
          onMouseOut={e => (e.currentTarget.style.backgroundColor = BRAND.red)}
        >
          <Calendar className="w-3.5 h-3.5" />
          {t('revenue.bookCta')}
        </a>
      </div>
    </motion.div>
  )
}

// ─── PageSpeedSection ─────────────────────────────────────────────────────────

const METRIC_LABELS: Record<string, string> = {
  lcp: 'Time to load main content (LCP)',
  fcp: 'Time to load first content (FCP)',
  tbt: 'Time the page was unresponsive (TBT)',
  si: 'Visual loading speed (SI)',
  cls: 'Visual stability (CLS)',
}

function MetricIndicator({ rating }: { rating: PageSpeedMetric['rating'] }) {
  if (rating === 'good') {
    return (
      <span className="flex h-2.5 w-2.5 shrink-0 items-center justify-center">
        <span className="h-2 w-2 rounded-full bg-[#77aa40]" />
      </span>
    )
  }
  const color = rating === 'poor' ? '#EF2020' : '#FF8C00'
  return (
    <span className="flex h-2.5 w-2.5 shrink-0 items-center justify-center">
      <svg aria-hidden="true" fill="none" height="8" viewBox="0 0 10 8" width="10" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 0L10 8H0L5 0Z" fill={color} />
      </svg>
    </span>
  )
}

function metricColor(rating: PageSpeedMetric['rating']): string {
  if (rating === 'good') return '#77aa40'
  if (rating === 'needs-improvement') return '#FF8C00'
  return '#ef2020'
}

function PageSpeedSection({ data, points }: { data: PageSpeedResult; points?: ScoreBreakdown }) {
  const { t } = useTranslation('report')
  const lcpMetric = data.metrics.find(m => m.id === 'lcp')
  const loadDisplay = lcpMetric?.display_value ?? `${data.load_time_seconds.toFixed(1)}s`
  const badgeColor = data.pass ? 'bg-[#e6f7e6] text-[#2d7a2d]' : 'bg-[#f7e6e6] text-[#ab4242]'
  const headlineColor = data.pass ? 'text-[#2d7a2d]' : 'text-[#ab4242]'

  return (
    <ReportSectionCard variant="nested" delay={0.27} data-testid="pagespeed-card">
      <ReportSectionHeader
        bordered={false}
        iconNode={
          <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.68 8.18c0-.57-.05-1.11-.14-1.64H8v3.1h4.3a3.67 3.67 0 0 1-1.59 2.41v2h2.57C16.85 12.65 15.68 8.18 15.68 8.18z" fill="#4285F4" />
            <path d="M8 16c2.16 0 3.97-.72 5.29-1.94l-2.57-2a4.8 4.8 0 0 1-2.72.75A4.79 4.79 0 0 1 3.49 9.5H.84v2.07A8 8 0 0 0 8 16z" fill="#34A853" />
            <path d="M3.49 9.5A4.84 4.84 0 0 1 3.24 8c0-.52.09-1.02.25-1.5V4.43H.84A8 8 0 0 0 0 8c0 1.29.31 2.51.84 3.57L3.49 9.5z" fill="#FBBC05" />
            <path d="M8 3.18c1.22 0 2.31.42 3.17 1.24l2.37-2.37A7.97 7.97 0 0 0 8 0a8 8 0 0 0-7.16 4.43L3.49 6.5A4.79 4.79 0 0 1 8 3.18z" fill="#EA4335" />
          </svg>
        }
        accentColor="#4285F4"
        title={`${t('pagespeed.title')} Google`}
        subtitle={t('pagespeed.subtitle', {
          time: loadDisplay,
          percent: data.percent_slower_than,
        })}
        right={
          <HeaderMetaChip>
            <span className={`font-semibold ${headlineColor}`} data-testid="pagespeed-headline-lcp">
              {loadDisplay}
            </span>
            {points && (
              <span
                className="ms-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium tabular-nums text-gray-600"
                data-testid="pagespeed-points"
              >
                {t('category.pts', { points: points.points, max: points.max_points })}
              </span>
            )}
            <span
              className={`ms-2 rounded px-2 py-0.5 text-[11px] font-medium ${badgeColor}`}
              data-testid="pagespeed-overall-badge"
            >
              {data.pass ? t('pagespeed.pass') : t('pagespeed.fail')}
            </span>
          </HeaderMetaChip>
        }
      />

      <div className="px-4 pb-5 sm:px-6">
      <div className="mt-2 w-full">
        <div className="grid grid-cols-2 gap-x-8 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
          {data.metrics.map((metric) => (
            <div key={metric.id} className="flex flex-col gap-2.5" data-testid={`pagespeed-metric-${metric.id}`}>
              <div className="flex items-center gap-2">
                <MetricIndicator rating={metric.rating} />
                <span
                  className="text-[26px] leading-[1.2] font-medium tracking-tight"
                  style={{ color: metricColor(metric.rating) }}
                >
                  {metric.display_value}
                </span>
              </div>
              <p className="text-xs font-medium text-black">
                {METRIC_LABELS[metric.id] ?? metric.id}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 h-px w-full bg-[#ddddde]" />
      <a
        className="mt-2 flex items-center gap-1.5 rounded-xl bg-white px-4 py-3 text-[13px] text-[#1e2939] hover:bg-[#fafafa]"
        data-testid="pagespeed-full-report-link"
        href={data.report_url}
        rel="noopener noreferrer"
        target="_blank"
      >
        {t('pagespeed.fullReport')}
        <svg aria-hidden="true" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 10L10 2M10 2H5M10 2v5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
      </div>
    </ReportSectionCard>
  )
}

// ─── Sticky consultation footer bar ──────────────────────────────────────────

function StickyConsultationBar({ businessName }: { businessName: string }) {
  const { t } = useTranslation('report')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <motion.div
      initial={false}
      animate={{ y: visible ? 0 : 80, opacity: visible ? 1 : 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="fixed bottom-0 left-0 right-0 z-[1100] pointer-events-none"
    >
      <div
        className="pointer-events-auto"
        style={{ background: `linear-gradient(90deg, ${BRAND.navy} 0%, #03224a 100%)` }}
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <p className="text-xs text-white font-semibold">
              {t('sticky.fixAllFull', { appName: APP_NAME, businessName })}
            </p>
            <p className="text-[10px] text-gray-400">{t('sticky.services')}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <p className="text-xs text-gray-300 sm:hidden">{t('sticky.fixAll', { appName: APP_NAME })}</p>
            <a
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white font-semibold text-xs px-5 py-2.5 rounded-xl transition-colors whitespace-nowrap"
              style={{ backgroundColor: BRAND.red }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = BRAND.redHover)}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = BRAND.red)}
            >
              <Calendar className="w-3.5 h-3.5" />
              {t('sticky.book')}
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// ─── ReportPage ───────────────────────────────────────────────────────────────

export function ReportPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const { t } = useTranslation('report')
  const [report, setReport] = useState<ReportPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!scanId) return
    scanApi.getReport(scanId)
      .then(setReport)
      .catch(() => setError(t('error.notReady')))
      .finally(() => setLoading(false))
  }, [scanId, t])

  if (loading) return <ReportLoading />
  if (error || !report) return <ReportError message={error} />

  return (
    <PhoneVerificationGate scanId={scanId!}>
      <ReportView report={report} />
    </PhoneVerificationGate>
  )
}

// ─── Shared loading / error states ────────────────────────────────────────────

export function ReportLoading() {
  return (
    <div className="min-h-screen bg-[#fafaf8] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
    </div>
  )
}

export function ReportError({ message }: { message?: string | null }) {
  const navigate = useI18nNavigate()
  const { t } = useTranslation('report')
  return (
    <div className="min-h-screen bg-[#fafaf8] flex items-center justify-center">
      <div className="text-center">
        <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 mb-4">{message ?? t('error.notFound')}</p>
        <button onClick={() => navigate('/')} className="text-sm hover:underline" style={{ color: BRAND.red }}>
          {t('error.newScan')}
        </button>
      </div>
    </div>
  )
}

// ─── ReportView ───────────────────────────────────────────────────────────────
// Renders the full report UI from an already-loaded payload. Used both by the
// public ReportPage (wrapped in the verification gate) and the authenticated
// customer portal (CustomerReportPage), which loads persisted reports from the
// database. `backPath` controls where the header back button / logo navigate.

export function ReportView({ report, backPath = '/' }: { report: ReportPayload; backPath?: string }) {
  const navigate = useI18nNavigate()
  const { t } = useTranslation('report')
  const { t: tCommon } = useTranslation('common')
  // Show a Dashboard shortcut in the header when a customer session is open.
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)

  const scorecard = report.scorecard_result
  const categoryScores = scorecard?.category_scores ?? {}
  const totalIssues = Object.values(categoryScores).reduce((sum, cat) => {
    return sum + (cat.breakdowns?.filter(b => !b.is_positive).length ?? 0)
  }, 0)
  const totalItems = Object.values(categoryScores).reduce((sum, cat) => {
    return sum + (cat.breakdowns?.length ?? 0)
  }, 0)

  return (
    <div className="min-h-screen bg-[#fafaf8] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-[0px_4px_2px_rgba(0,0,0,0.13)]">
        <div className="mx-auto flex min-h-[80px] max-w-6xl items-center justify-between gap-3 px-3 py-3 sm:gap-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="shrink-0 text-[#99a1af] transition-colors hover:text-[#364153]"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => navigate(backPath)} className="shrink-0">
              <img src={logoHeader} alt={APP_NAME} className="h-9 w-auto sm:h-11 md:h-[52px]" />
            </button>
            <span className="hidden h-4 w-px shrink-0 bg-[#e5e7eb] sm:block" aria-hidden />
            <p className="hidden min-w-0 truncate text-sm text-[#6a7282] sm:block">
              {report.business_name}
            </p>
          </div>

          <nav className="flex shrink-0 items-center justify-end gap-1.5 sm:gap-3">
            <LanguageSwitcher variant="compact" />
            <a
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center justify-center gap-1.5 rounded-[10px] px-3 text-xs font-medium text-white transition-colors whitespace-nowrap md:inline-flex md:h-[34px] md:px-4 md:text-sm"
              style={{ backgroundColor: BRAND.red }}
              onMouseOver={e => { e.currentTarget.style.backgroundColor = BRAND.redHover }}
              onMouseOut={e => { e.currentTarget.style.backgroundColor = BRAND.red }}
            >
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              {t('header.freeConsultation')}
            </a>
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                aria-label={tCommon('nav.dashboard')}
                className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] border border-[#01114f] bg-white px-0 text-xs font-medium text-[#01114f] transition-colors hover:bg-[#f2f2f6] whitespace-nowrap md:h-[34px] md:w-auto md:px-4 md:text-sm"
              >
                <LayoutDashboard className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{tCommon('nav.dashboard')}</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(`/search?q=${encodeURIComponent(report.business_name)}`)}
              aria-label={t('header.rescan')}
              className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] border border-[#01114f] bg-white px-0 text-xs font-medium text-[#01114f] transition-colors hover:bg-[#f2f2f6] whitespace-nowrap md:h-[34px] md:w-auto md:px-4 md:text-sm"
            >
              <RefreshCw className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden md:inline">{t('header.rescan')}</span>
            </button>
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => navigate('/')}
                aria-label={tCommon('nav.search')}
                className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-[10px] border border-gray-200 bg-white px-0 text-xs font-medium text-[#4a5565] transition-colors hover:bg-[#f2f2f6] whitespace-nowrap md:h-[34px] md:w-auto md:px-4 md:text-sm"
              >
                <Search className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{tCommon('nav.search')}</span>
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* Report layout — single column on mobile/tablet, sidebar + main on desktop */}
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8 lg:flex-row lg:gap-8">
        {/* Left: sticky sidebar (stacks on top on mobile/tablet) */}
        <CategorySidebar
          overallScore={report.overall_score}
          categoryScores={categoryScores}
          businessName={report.business_name}
        />

        {/* Right: main content */}
        <main className="flex-1 min-w-0">
          {/* Revenue loss banner */}
          <RevenueLossBanner report={report} />

          {/* AI Summary + Competitors (Figma two-column row) */}
          <ReportOverviewGrid
            aiSummary={report.ai_summary}
            competitors={report.competitors ?? []}
          />

          {/* Summary header */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              {t('summary.title', { total: totalItems, issues: totalIssues })}
            </h1>
            <p className="text-sm text-gray-500">{t('summary.subtitle', { appName: APP_NAME })}</p>
          </motion.div>

          {/* Recommendations */}
          {report.ai_summary?.recommendations && (
            <RecommendationsSection recommendations={report.ai_summary.recommendations} />
          )}

          {/* Review Intelligence */}
          {report.review_analysis ? (
            <ReviewIntelligenceSection
              analysis={report.review_analysis}
              averageRating={report.reviews?.average_rating ?? 0}
              totalCount={report.reviews?.total_count ?? 0}
            />
          ) : report.reviews?.average_rating != null ? (
            <ReviewRatingFallback
              averageRating={report.reviews.average_rating}
              totalCount={report.reviews.total_count ?? 0}
            />
          ) : null}

          {/* Business Photos */}
          {report.photos?.photos && report.photos.photos.length > 0 && (
            <PhotosSection photos={report.photos.photos} totalCount={report.photos.total_count} />
          )}

          {/* Search Rankings */}
          {report.search_rankings && report.search_rankings.length > 0 && (
            <SearchRankingsSection
              rankings={report.search_rankings}
              scanId={report.scan_id}
            />
          )}

          {/* Category sections */}
          {CATEGORY_ORDER.map((key, i) => {
            const cat = categoryScores[key]
            if (!cat) return null
            let beforeBreakdowns: React.ReactNode = undefined
            if (key === 'guest_experience' && report.page_speed) {
              const psBreakdown = cat.breakdowns?.find(
                b => b.signal === 'page_speed' || b.signal === 'page_speed_unavailable',
              )
              beforeBreakdowns = <PageSpeedSection data={report.page_speed} points={psBreakdown} />
            } else if (key === 'local_listings' && report.profile) {
              // Review signals are shown here under User-submitted content as
              // display-only context (no longer scored under Search Results).
              beforeBreakdowns = (
                <LocalListingsProfileCard
                  profile={report.profile}
                  reviewCount={report.reviews?.total_count}
                  averageRating={report.reviews?.average_rating}
                  recentReviewCount={report.reviews?.recent_count_30_days}
                  breakdowns={cat.breakdowns}
                />
              )
            }
            return (
              <CategorySection
                key={key}
                number={i + 1}
                category={cat}
                seoAnalysis={report.seo_analysis}
                seoAudit={report.seo_audit}
                beforeBreakdowns={beforeBreakdowns}
              />
            )

          })}

          {/* AI Website Builder entry point (replaces the consultation CTA) */}
          <ImproveWebsiteCTA report={report} />

          {/* Phone mockup + Cartley website preview */}
          <CartleyWebsitePreview report={report} />
        </main>
      </div>

      {/* Sticky consultation footer */}
      <StickyConsultationBar businessName={report.business_name} />
    </div>
  )
}
